import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface Admin {
  id_admin: number;
  email: string;
  nome: string;
}

interface AuthContextType {
  admin: Admin | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, senha: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL = process.env.REACT_APP_API_URL || 'https://tribhus.net';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Verificar se há token salvo no localStorage
    const savedToken = localStorage.getItem('admin_token');
    const savedAdmin = localStorage.getItem('admin_data');

    if (savedToken && savedAdmin) {
      verifyToken(savedToken, JSON.parse(savedAdmin));
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async (savedToken: string, savedAdmin: Admin) => {
    try {
      // ALTO-009b: withCredentials envia o cookie httpOnly; Bearer ainda vai junto
      // durante fase 1 para sessoes legadas que so tem token no localStorage.
      const response = await axios.get(`${API_URL}/admin-auth/verify`, {
        withCredentials: true,
        headers: savedToken ? { Authorization: `Bearer ${savedToken}` } : undefined,
      });

      if (response.data.valid) {
        setToken(savedToken);
        setAdmin(response.data.admin || savedAdmin);
      } else {
        // Token inválido, limpar dados
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_data');
      }
    } catch (error) {
      // Token expirado ou inválido
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_data');
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, senha: string): Promise<{ success: boolean; error?: string }> => {
    try {
      // ALTO-009b: withCredentials recebe o cookie httpOnly setado no backend.
      const response = await axios.post(
        `${API_URL}/admin-auth/login`,
        { email, senha },
        { withCredentials: true }
      );

      if (response.data.success) {
        const { token: newToken, admin: adminData } = response.data;

        // Salvar no state. ALTO-009b: cookie e a fonte da sessao; localStorage
        // mantem apenas cache do admin_data para render inicial. Token continua
        // sendo gravado em fase 1 para compatibilidade com codigo legado que
        // ainda possa ler admin_token; fase 2 removera esta gravacao.
        setToken(newToken);
        setAdmin(adminData);
        localStorage.setItem('admin_token', newToken);
        localStorage.setItem('admin_data', JSON.stringify(adminData));

        return { success: true };
      }

      return { success: false, error: 'Erro desconhecido' };
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Erro ao conectar com o servidor';
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    setToken(null);
    setAdmin(null);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_data');
    // ALTO-009b: limpa tambem o cookie HttpOnly no backend (fire-and-forget).
    axios.post(`${API_URL}/admin-auth/logout`, {}, { withCredentials: true }).catch(() => {});
  };

  return (
    <AuthContext.Provider
      value={{
        admin,
        token,
        isAuthenticated: !!token && !!admin,
        isLoading,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
